import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import type { AppRouteContext } from '../../../app/routeContext';
import { getDeploySurface, getWrongAppInfo, surfaceRequiresPrivilegedLogin, SURFACE_APP_TITLE } from '../../../lib/deploySurface';
import { isRoleAllowedOnSurface } from '../../../lib/deploySurface';
const LoginPage = React.lazy(() => import('./LoginPage'));
import WrongAppScreen from '../components/WrongAppScreen';
import type { AuthLoginResponse } from '../types/auth.types';
import { authApi } from '../api/auth.api';
import { normalizeRole } from '../rbac';

type StaffSignInPageProps = {
  homePath: string;
};

export default function StaffSignInPage({ homePath }: StaffSignInPageProps) {
  const navigate = useNavigate();
  const context = useOutletContext<AppRouteContext>();
  const location = useLocation();
  const surface = getDeploySurface();
  const user = context.core.currentUser;
  const [wrongAppAfterLogin, setWrongAppAfterLogin] = useState<ReturnType<typeof getWrongAppInfo>>(null);

  const attemptedPath = (location.state as { from?: string })?.from || homePath;

  if (user) {
    const wrongApp = getWrongAppInfo(user.role, surface);
    if (wrongApp) {
      return (
        <WrongAppScreen
          info={wrongApp}
          onSignOut={() => {
            void context.core.logout?.();
          }}
        />
      );
    }
    if (isRoleAllowedOnSurface(user.role, surface)) {
      return <Navigate to={attemptedPath} replace />;
    }
  }

  const title =
    surface !== 'website' && surface in SURFACE_APP_TITLE
      ? SURFACE_APP_TITLE[surface as keyof typeof SURFACE_APP_TITLE]
      : 'Ninpo';

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md mb-6 text-center space-y-2">
        <p className="text-xs uppercase tracking-[0.35em] text-white/50">Staff sign-in</p>
        <h1 className="text-xl font-black text-white">{title}</h1>
      </div>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-black/40 p-6">
        {wrongAppAfterLogin ? (
          <WrongAppScreen
            info={wrongAppAfterLogin}
            onSignOut={() => {
              setWrongAppAfterLogin(null);
              void context.core.logout?.();
            }}
          />
        ) : (
          <React.Suspense
            fallback={
              <div className="flex h-64 items-center justify-center rounded-2xl p-8 text-sm text-slate-400">
                Loading sign in…
              </div>
            }
          >
            <LoginPage
              requireMfa={surfaceRequiresPrivilegedLogin(surface)}
              onSuccess={async (loginResponse: AuthLoginResponse) => {
                const normalizedRole = normalizeRole(loginResponse?.data?.user?.role);
                if (!isRoleAllowedOnSurface(normalizedRole, surface)) {
                  await authApi.logout().catch(() => {});
                  setWrongAppAfterLogin(getWrongAppInfo(normalizedRole, surface));
                  return;
                }
                const applied = context.core.applyLoginSession
                  ? await context.core.applyLoginSession(loginResponse)
                  : false;
                if (!applied) return;
                await context.core.fetchOrders?.().catch(() => {});
              }}
              onCancel={() => navigate('/')}
            />
          </React.Suspense>
        )}
      </div>
    </div>
  );
}
